import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import axiosAPI from "Includes/api/api";
import { attachGooglePlaceSearch, loadGoogleMaps } from "Includes/googleMaps";

import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  TextField,
  Switch,
  InputAdornment,
  Tooltip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";

import {
  Search,
  LocationOn,
  School,
  AccountBalance,
  CheckCircle,
  Business,
  Link as LinkIcon,
} from "@mui/icons-material";

import "./SchoolCollegeSync.css";
import HistoricalImportModal from "./HistoricalImportModal";

const TYPE_COLORS = { SCHOOL: "#1976d2", COLLEGE: "#7b1fa2" };
const CLIENT_COLOR = "#2e7d32";
const STORED_PATH = "/api/area-search/stored/";
const PRIORITY_STATUSES = new Set(["interested", "followup"]);

const INTEREST_META = {
  interested: { emoji: "⭐", label: "Interested" },
  not_interested: { emoji: "⛔", label: "Not interested" },
  pending: { emoji: "📌", label: "Pending" },
  followup: { emoji: "🔔", label: "Follow-up" },
  go_to_main_branch: { emoji: "🏢", label: "Go to main branch" },
};

const coverageMarkerIcon = (maps, type, isClient, interestStatus) => {
  const color = isClient ? CLIENT_COLOR : (TYPE_COLORS[type] || "#1976d2");
  const interest = INTEREST_META[interestStatus];
  const size = interest ? 28 : (isClient ? 16 : 12);
  const label = interest ? interest.emoji : "";
  const svg = label
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><text x="4" y="22" font-size="20">${label}</text></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}"><circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1}" fill="${color}" stroke="white" stroke-width="2"/></svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new maps.Size(size, size),
    anchor: new maps.Point(size / 2, size / 2),
  };
};

const CoverageMap = ({ institutions, selectedInstitution }) => {
  const mapRef = useRef(null);
  const searchRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((maps) => {
      if (cancelled || !mapRef.current || mapInstanceRef.current) return;
      mapInstanceRef.current = new maps.Map(mapRef.current, {
        center: { lat: 12.9716, lng: 77.5946 },
        zoom: 11,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: "greedy",
      });
      setMapReady(true);
    }).catch(() => {});
    return () => {
      cancelled = true;
      mapInstanceRef.current = null;
      if (mapRef.current) mapRef.current.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    attachGooglePlaceSearch(searchRef.current, (place) => {
      const map = mapInstanceRef.current;
      if (!map || cancelled) return;
      map.panTo({ lat: place.lat, lng: place.lng });
      map.setZoom(15);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((maps) => {
      const map = mapInstanceRef.current;
      if (cancelled || !map) return;
      markersRef.current.forEach((marker) => marker.setMap(null));
      markersRef.current = [];
      const bounds = new maps.LatLngBounds();
      const info = new maps.InfoWindow();
      institutions.forEach((inst) => {
        if (!inst.lat || !inst.lng) return;
        const position = { lat: Number(inst.lat), lng: Number(inst.lng) };
        bounds.extend(position);
        const isClient = inst.client_status === "Y";
        const interest = INTEREST_META[inst.interest_status];
        const interestLine = interest
          ? `<small>${interest.emoji} ${interest.label}${inst.last_visit_date ? ` · last visit ${inst.last_visit_date}` : ""}</small><br>`
          : `<small>No visit interest yet</small><br>`;
        const marker = new maps.Marker({
          map,
          position,
          icon: coverageMarkerIcon(maps, inst.type || inst.institution_type, isClient, inst.interest_status),
          zIndex: PRIORITY_STATUSES.has(inst.interest_status) ? 1000 : 1,
        });
        marker.addListener("click", () => {
          info.setContent(
            `<b>${inst.name || ""}</b><br>
             <small>${inst.type || ""}</small><br>
             <small>${inst.address || ""}</small><br>
             ${interestLine}
             <small style="color:${isClient ? CLIENT_COLOR : "#888"}">${isClient ? "✓ Our Client" : "Prospect"}</small>
             ${inst.company_name ? `<br><small>Company: ${inst.company_name}</small>` : ""}`
          );
          info.open(map, marker);
        });
        markersRef.current.push(marker);
      });
      if (!bounds.isEmpty()) map.fitBounds(bounds, 40);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [institutions, mapReady]);

  useEffect(() => {
    if (selectedInstitution?.lat && selectedInstitution?.lng && mapInstanceRef.current) {
      mapInstanceRef.current.panTo({ lat: Number(selectedInstitution.lat), lng: Number(selectedInstitution.lng) });
      mapInstanceRef.current.setZoom(16);
    }
  }, [selectedInstitution]);

  return (
    <Card className="sc-map-card" style={{ marginTop: 16 }}>
      <Box style={{ padding: 16, borderBottom: "1px solid #eee", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <Box>
          <Typography className="sc-results-title">Coverage Map</Typography>
          <Typography className="sc-results-subtitle">Emoji shows the interest saved on the latest visit</Typography>
        </Box>
        <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Chip size="small" style={{ backgroundColor: TYPE_COLORS.SCHOOL, color: "#fff" }} label="School" />
          <Chip size="small" style={{ backgroundColor: TYPE_COLORS.COLLEGE, color: "#fff" }} label="College" />
          <Chip size="small" style={{ backgroundColor: CLIENT_COLOR, color: "#fff" }} label="Our Client" />
          {Object.entries(INTEREST_META).map(([key, meta]) => (
            <Chip key={key} size="small" variant="outlined" label={`${meta.emoji} ${meta.label}`} />
          ))}
        </Box>
      </Box>
      <Box style={{ padding: "12px 16px 0" }}>
        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          placeholder="Private schools, colleges, or locations in Karnataka"
        />
      </Box>
      <Box ref={mapRef} style={{ width: "100%", height: 420 }} />
    </Card>
  );
};

const SchoolCollegeSync = () => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all"); // all | school | college
  const [clientFilter, setClientFilter] = useState("all"); // all | yes | no
  const [interestFilter, setInterestFilter] = useState("all");
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [listMode, setListMode] = useState("active"); // active | blocked
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [updatingId, setUpdatingId] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingInst, setPendingInst] = useState(null);
  const [companyQuery, setCompanyQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const fetchStored = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (appliedSearch.trim()) params.q = appliedSearch.trim();
      if (typeFilter !== "all") params.type = typeFilter;
      if (clientFilter !== "all") params.client = clientFilter;
      if (listMode === "blocked") params.blocked = "yes";

      const resp = await axiosAPI.get(STORED_PATH, { params });
      const list = resp.data?.data || [];
      setInstitutions(list);
      setPage(0);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || "Failed to load schools from database.");
      setInstitutions([]);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, typeFilter, clientFilter, listMode]);

  useEffect(() => {
    fetchStored();
  }, [fetchStored]);

  const handleSearch = () => {
    setAppliedSearch(searchText);
  };

  const openCompanyPicker = (inst) => {
    setPendingInst(inst);
    setCompanyQuery("");
    setPickerOpen(true);
  };

  const closeCompanyPicker = () => {
    setPickerOpen(false);
    setPendingInst(null);
    setCompanyQuery("");
  };

  useEffect(() => {
    if (!pickerOpen) return undefined;
    let mounted = true;
    const handle = window.setTimeout(async () => {
      setCompaniesLoading(true);
      try {
        const params = { limit: 30, pageno: 1, status: "active" };
        if (companyQuery.trim()) params.name = companyQuery.trim();
        const resp = await axiosAPI.get("company/company/", { params });
        if (!mounted) return;
        const list = resp.data?.data || resp.data?.results || [];
        setCompanies(Array.isArray(list) ? list : []);
      } catch (e) {
        if (mounted) setCompanies([]);
      } finally {
        if (mounted) setCompaniesLoading(false);
      }
    }, 250);
    return () => {
      mounted = false;
      window.clearTimeout(handle);
    };
  }, [pickerOpen, companyQuery]);

  const applyCompany = async (company) => {
    if (!pendingInst) return;
    setUpdatingId(pendingInst.id);
    try {
      const resp = await axiosAPI.patch(STORED_PATH, {
        id: pendingInst.id,
        client_status: "Y",
        company_id: company.id,
      });
      const saved = resp.data?.data || {};
      setInstitutions((prev) =>
        prev.map((item) => (
          item.id === pendingInst.id
            ? {
              ...item,
              client_status: "Y",
              company_id: saved.company_id || company.id,
              company_name: saved.company_name || company.name,
              company_code: saved.company_code || company.code || "",
            }
            : item
        ))
      );
      closeCompanyPicker();
    } catch (e) {
      alert(e.response?.data?.error || e.message || "Failed to update client status");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleMarkClient = async (inst, isClient) => {
    if (isClient) {
      openCompanyPicker(inst);
      return;
    }
    setUpdatingId(inst.id);
    try {
      await axiosAPI.patch(STORED_PATH, { id: inst.id, client_status: "" });
      setInstitutions((prev) =>
        prev.map((item) => (
          item.id === inst.id
            ? { ...item, client_status: "", company_id: null, company_name: "", company_code: "" }
            : item
        ))
      );
    } catch (e) {
      alert(e.response?.data?.error || e.message || "Failed to update client status");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleUnblock = async (inst) => {
    setUpdatingId(inst.id);
    try {
      await axiosAPI.patch(STORED_PATH, { id: inst.id, is_blocked: false });
      setInstitutions((prev) => prev.filter((item) => item.id !== inst.id));
    } catch (e) {
      alert(e.response?.data?.error || e.message || "Failed to unblock this school");
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredInstitutions = useMemo(() => {
    if (interestFilter === "all") return institutions;
    if (interestFilter === "none") return institutions.filter((item) => !item.interest_status);
    return institutions.filter((item) => item.interest_status === interestFilter);
  }, [institutions, interestFilter]);

  const visibleStats = useMemo(() => ({
    total: filteredInstitutions.length,
    school_count: filteredInstitutions.filter((item) => (item.type || "").toUpperCase() === "SCHOOL").length,
    college_count: filteredInstitutions.filter((item) => (item.type || "").toUpperCase() === "COLLEGE").length,
    client_count: filteredInstitutions.filter((item) => item.client_status === "Y").length,
  }), [filteredInstitutions]);

  useEffect(() => {
    setPage(0);
  }, [interestFilter]);

  const pagedRows = useMemo(
    () => filteredInstitutions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredInstitutions, page, rowsPerPage]
  );

  return (
    <Box className="schools-colleges-page">
      <Box className="sc-header">
        <Box>
          <Typography className="sc-title">Schools &amp; Colleges</Typography>
          <Typography className="sc-subtitle">
            Browse institutions stored in your database and mark your clients
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => setImportModalOpen(true)}
            startIcon={<LinkIcon />}
          >
            Load from Maps link
          </Button>
          <Button variant="outlined" size="small" onClick={fetchStored} disabled={loading} startIcon={loading ? <CircularProgress size={14} /> : <Search />}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Card className="sc-search-card">
        <CardContent>
          <Typography className="sc-section-title">Filter Database</Typography>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={5}>
              <Typography className="sc-label">Search name / area</Typography>
              <TextField
                size="small"
                fullWidth
                placeholder="e.g. Yelahanka or school name"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn className="location-icon" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography className="sc-label">Type</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={typeFilter}
                onChange={(_, v) => v && setTypeFilter(v)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="school"><School fontSize="small" /> School</ToggleButton>
                <ToggleButton value="college"><AccountBalance fontSize="small" /> College</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography className="sc-label">Client</Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={clientFilter}
                onChange={(_, v) => v && setClientFilter(v)}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="yes">Clients</ToggleButton>
                <ToggleButton value="no">Prospects</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                className="sc-search-button"
                onClick={handleSearch}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Search />}
              >
                {loading ? "Loading..." : "Search"}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Typography className="sc-label">Last visit interest</Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label="All"
                  size="small"
                  color={interestFilter === "all" ? "primary" : "default"}
                  variant={interestFilter === "all" ? "filled" : "outlined"}
                  onClick={() => setInterestFilter("all")}
                />
                {Object.entries(INTEREST_META).map(([key, meta]) => (
                  <Chip
                    key={key}
                    label={`${meta.emoji} ${meta.label}`}
                    size="small"
                    color={interestFilter === key ? "primary" : "default"}
                    variant={interestFilter === key ? "filled" : "outlined"}
                    onClick={() => setInterestFilter(key)}
                  />
                ))}
                <Chip
                  label="No visit"
                  size="small"
                  color={interestFilter === "none" ? "primary" : "default"}
                  variant={interestFilter === "none" ? "filled" : "outlined"}
                  onClick={() => setInterestFilter("none")}
                />
              </Box>
            </Grid>
          </Grid>
          {error && <Typography className="sc-error">{error}</Typography>}
          <Box className="sc-google-info">
            <Business />
            <Typography>
              Data comes from Visit Planner searches saved in your database. Toggle <b>Client</b> and choose the matching institute from the company list.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={2} className="sc-summary">
        <Grid item xs={12} sm={3}>
          <Card className="sc-stat-card">
            <CardContent>
              <Box className="sc-stat-icon school"><School /></Box>
              <Box>
                <Typography className="sc-stat-number">{visibleStats.school_count}</Typography>
                <Typography className="sc-stat-title">Schools</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="sc-stat-card">
            <CardContent>
              <Box className="sc-stat-icon college"><AccountBalance /></Box>
              <Box>
                <Typography className="sc-stat-number">{visibleStats.college_count}</Typography>
                <Typography className="sc-stat-title">Colleges</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="sc-stat-card">
            <CardContent>
              <Box className="sc-stat-icon total"><CheckCircle /></Box>
              <Box>
                <Typography className="sc-stat-number">{visibleStats.client_count}</Typography>
                <Typography className="sc-stat-title">Our Clients</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card className="sc-stat-card">
            <CardContent>
              <Box className="sc-stat-icon total"><Business /></Box>
              <Box>
                <Typography className="sc-stat-number">{visibleStats.total}</Typography>
                <Typography className="sc-stat-title">Showing</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <CoverageMap institutions={filteredInstitutions} selectedInstitution={selectedInstitution} />

      <Card className="sc-results-card">
        <Box className="sc-results-header">
          <Box>
            <Typography className="sc-results-title">Stored Institutions</Typography>
            <Typography className="sc-results-subtitle">
              {listMode === "blocked"
                ? "Schools hidden from Visit Planner. Unblock to show them again."
                : "Check and mark your client schools & colleges"}
            </Typography>
          </Box>
          <Chip label={`${filteredInstitutions.length} results`} className="sc-results-chip" />
        </Box>
        <Tabs
          value={listMode}
          onChange={(_, value) => value && setListMode(value)}
          sx={{ px: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="active" label="All" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab value="blocked" label="Blocked" sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Address</TableCell>
                {listMode === "blocked" && <TableCell>Reason</TableCell>}
                <TableCell>Interest</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Company</TableCell>
                <TableCell align="center">{listMode === "blocked" ? "Blocked" : "Our Client"}</TableCell>
                <TableCell align="center">Map</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={listMode === "blocked" ? 10 : 9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : pagedRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={listMode === "blocked" ? 10 : 9} align="center">
                    <Box className="sc-empty">
                      <School />
                      <Typography>
                        {institutions.length === 0
                          ? (listMode === "blocked" ? "No blocked schools" : "No institutions in database")
                          : "No institutions match this interest"}
                      </Typography>
                      <Typography variant="body2">
                        {listMode === "blocked"
                          ? "Block a school from the day-wise plan in Visit Planner to hide it here."
                          : "Search areas in Visit Planner to populate this list."}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                pagedRows.map((inst) => {
                  const isClient = inst.client_status === "Y";
                  const type = (inst.type || inst.institution_type || "SCHOOL").toUpperCase();
                  const interest = INTEREST_META[inst.interest_status];
                  return (
                    <TableRow
                      key={inst.id}
                      hover
                      selected={selectedInstitution?.id === inst.id}
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSelectedInstitution(inst)}
                    >
                      <TableCell>{inst.id}</TableCell>
                      <TableCell>
                        <Typography className="sc-institution-name">{inst.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {type === "COLLEGE" ? (
                          <Chip icon={<AccountBalance />} label="COLLEGE" size="small" className="sc-college-chip" />
                        ) : (
                          <Chip icon={<School />} label="SCHOOL" size="small" className="sc-school-chip" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography className="sc-address">{inst.address || "—"}</Typography>
                      </TableCell>
                      {listMode === "blocked" && (
                        <TableCell>
                          <Typography className="sc-address">{inst.block_reason || "—"}</Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {interest ? (
                          <Chip size="small" variant="outlined" label={`${interest.emoji} ${interest.label}`} />
                        ) : (
                          <Chip size="small" variant="outlined" label="No visit" />
                        )}
                      </TableCell>
                      <TableCell>
                        {isClient ? (
                          <Chip icon={<CheckCircle />} label="Our Client" size="small" color="success" />
                        ) : (
                          <Chip label="Prospect" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {inst.company_name ? (
                          <Button
                            size="small"
                            sx={{ textTransform: "none", justifyContent: "flex-start", px: 0 }}
                            onClick={() => openCompanyPicker(inst)}
                          >
                            {inst.company_name}
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {listMode === "blocked" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={updatingId === inst.id}
                            onClick={() => handleUnblock(inst)}
                          >
                            {updatingId === inst.id ? "..." : "Unblock"}
                          </Button>
                        ) : (
                          <Tooltip title={isClient ? "Unmark as client" : "Mark as our client"}>
                            <Switch
                              color="success"
                              size="small"
                              checked={isClient}
                              disabled={updatingId === inst.id}
                              onChange={(e) => handleMarkClient(inst, e.target.checked)}
                            />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Button size="small" onClick={() => setSelectedInstitution(inst)}>View</Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredInstitutions.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Card>

      <Dialog open={pickerOpen} onClose={closeCompanyPicker} fullWidth maxWidth="sm">
        <DialogTitle>Which institute?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Choose the matching institute from the company list
            {pendingInst?.name ? ` for ${pendingInst.name}` : ""}.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search institute name or code"
            value={companyQuery}
            onChange={(e) => setCompanyQuery(e.target.value)}
          />
          <List sx={{ mt: 1, maxHeight: 320, overflow: "auto" }}>
            {companiesLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : companies.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, px: 1 }}>
                No institutes match.
              </Typography>
            ) : (
              companies.map((company) => (
                <ListItemButton
                  key={company.id}
                  disabled={updatingId === pendingInst?.id}
                  onClick={() => applyCompany(company)}
                >
                  <ListItemText
                    primary={company.name}
                    secondary={company.code || company.domain || ""}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCompanyPicker}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <HistoricalImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportSuccess={() => fetchStored()}
      />
    </Box>
  );
};

export default SchoolCollegeSync;
